import type { LayerStackSprite } from '../../layerstack/LayerStackSprite';
import type { StateName } from '../../layerstack/ManifestTypes';

/**
 * Damage and fire reads use the states the manifests already ship — no new art
 * and no tint hack. A flash swaps the state for a while and puts back whatever
 * the sprite was showing before the FIRST trigger, so retriggering mid-flash
 * extends the flash instead of latching the flash state as the resting one.
 */
export const ENEMY_HIT_FLASH_MS = 160;
export const PLAYER_DAMAGED_FLASH_MS = 280;
export const PLAYER_FIRING_FLASH_MS = 200;

interface ActiveFlash {
  readonly revertTo: StateName;
  remainingMs: number;
}

export class StateFlash {
  private readonly active = new Map<LayerStackSprite, ActiveFlash>();

  get activeCount(): number {
    return this.active.size;
  }

  /** A state the manifest does not author is skipped: `setState` would throw. */
  flash(sprite: LayerStackSprite, state: string, ms: number, revertTo?: StateName): void {
    if (!sprite.states.includes(state)) return;
    const running = this.active.get(sprite);
    if (running) running.remainingMs = ms;
    else this.active.set(sprite, { revertTo: revertTo === undefined ? sprite.stateName : revertTo, remainingMs: ms });
    sprite.setState(state);
  }

  /** No auto-revert — the caller decides when the sprite comes back. */
  hold(sprite: LayerStackSprite, state: string): void {
    if (!sprite.states.includes(state)) return;
    this.active.delete(sprite);
    sprite.setState(state);
  }

  update(deltaMs: number): void {
    for (const [sprite, flash] of [...this.active]) {
      flash.remainingMs -= deltaMs;
      if (flash.remainingMs > 0) continue;
      this.active.delete(sprite);
      sprite.setState(flash.revertTo);
    }
  }

  clear(): void {
    this.active.clear();
  }
}
